import type { Params } from '@vimcar/mvvm/linkViewModel'
import type { ViewState } from '#components/Root/viewModel'
import type { Transitioner } from '#components/TransitionProvider'

import emailRegExp from 'email-regex'
import * as mobx from 'mobx'
import PT from 'prop-types'
import * as o from 'rxjs/operators'

import type { Pipedrive, LeadInfo } from '../../models/Pipedrive'
import type { Zendesk, TicketInfo } from '../../models/Zendesk'

import {
  ATTRIBUTE_CAR,
  ATTRIBUTE_ENGINE,
  ATTRVALUE_DESTINATION_PIPEDRIVE,
  ATTRVALUE_DESTINATION_ZENDESK,
} from '../../utils/constants'
import i18n from '../../utils/i18n'

export type Props = {
  state: ViewState
}

type Validator = Array<[(value: number) => boolean | null | undefined, string]>

export type ViewModel = {
  data: {
    name?: string
    requestEmail?: string
    telephone?: string
    message?: string
  }
  loading: boolean
  showGivenInfo: boolean
  givenCar?: string | null
  givenEngine?: string | null
  emailValidator: Validator
  phoneValidator: Validator
  isValid: boolean

  handleSend: () => void
  handleFormValid: (boolean) => void
  handleFormChange: (Object) => void
}

type Context = {
  models: { zendesk: Zendesk; pipedrive: Pipedrive }
  transitioner: Transitioner
}

const mkViewModel = (
  { registerAutorun, registerObservable }: Params,
  { state }: Props,
  { models: { zendesk, pipedrive }, transitioner }: Context,
): ViewModel => {
  const attrs = transitioner.getAttributes()
  const user = attrs && attrs.user
  const vm = mobx.observable({
    data: user
      ? {
          name: user.fullName,
          requestEmail: user.email,
        }
      : null,
    givenCar: null,
    givenEngine: null,
    loading: false,
    get showGivenInfo() {
      return vm.givenCar || vm.givenEngine
    },

    emailValidator: mobx.observable.ref([
      [
        (value) => emailRegExp({ exact: true }).test(value),
        i18n.translateWithoutTestId('Global.invalid-email'),
      ],
    ]),
    phoneValidator: mobx.observable.ref([
      [
        (value) =>
          value === null ||
          value === '' ||
          new RegExp('^[0-9-\+\(\) ]*$').test(value),
        i18n.translateWithoutTestId('Global.invalid-phone'),
      ],
    ]),

    isValid: false,
    handleSend: mobx.action(() => {
      vm.loading = true
      const nextNodeId =
        state.currentNode &&
        state.currentNode.nextNode &&
        state.currentNode.nextNode.id
      const values = transitioner.getValueCollection()
      if (values.destination === ATTRVALUE_DESTINATION_ZENDESK) {
        const ticketPayload: TicketInfo = {
          ...values,
          ...vm.data,
          // @ts-expect-error@2322
          customValues: { ...state.customAttributesObject },
        }
        registerObservable(
          zendesk.createTicket(ticketPayload).pipe(
            o.tap(() => {
              vm.loading = false
              if (nextNodeId) transitioner.goForward(nextNodeId)
            }),
          ),
        )
      } else if (values.destination === ATTRVALUE_DESTINATION_PIPEDRIVE) {
        const leadPayload: LeadInfo = {
          ...values,
          ...vm.data,
          customValues: { ...state.customAttributesObject },
        }
        registerObservable(
          pipedrive.createLead(leadPayload).pipe(
            o.tap(() => {
              vm.loading = false
              if (nextNodeId) transitioner.goForward(nextNodeId)
            }),
          ),
        )
      }
    }),
    handleFormValid: mobx.action((isValid) => {
      // @ts-expect-error@2322
      vm.isValid = isValid
    }),
    handleFormChange: mobx.action((values) => {
      // @ts-expect-error@2339
      const { name, requestEmail, telephone, message } = values
      // @ts-expect-error@2353
      vm.data = { name, requestEmail, telephone, message }
    }),
  })

  registerAutorun(() => {
    const values = transitioner.getValueCollection()
    vm.givenCar = values.attrs.get(ATTRIBUTE_CAR)
    vm.givenEngine = values.attrs.get(ATTRIBUTE_ENGINE)
  })
  // @ts-expect-error@2322
  return vm
}

mkViewModel.contextTypes = {
  models: PT.object.isRequired,
  transitioner: PT.object.isRequired,
}

export default mkViewModel
